/**
 * Marketing Onboarding Context
 *
 * State management for the guided onboarding flow that introduces
 * new users to the marketing module through ad generation.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@trafiflow/shared-ui'
import { adGeneratorService } from '@/services/ad-generator'
import { competitorAnalysisService } from '@/services/competitor-analysis'
import apiClient from '@/shared/lib/api'
import { ROUTES } from '@/shared/config'
import type { AdGenJob, AdGenVariant } from '@/services/ad-generator/types'
import type { CompAnalysisResult } from '@/services/competitor-analysis/types'

export type OnboardingStatus = 'loading' | 'brand_select' | 'pipeline_running' | 'presenting' | 'completed'

export interface OnboardingArtifacts {
  brand: Record<string, unknown> | null
  metaIdentity: Record<string, unknown> | null
  adsCollection: Record<string, unknown> | null
  patterns: Record<string, unknown> | null
  strategy: Record<string, unknown> | null
  blueprint: Record<string, unknown> | null
  clipBank: Record<string, unknown> | null
  winner: AdGenVariant | null
  // Rich competitor analysis from dedicated competitor-analysis pipeline
  competitorAnalysis: CompAnalysisResult | null
  // Product Bible - comprehensive product knowledge base with buyer personas
  productBible: Record<string, unknown> | null
}

export interface OnboardingState {
  status: OnboardingStatus
  selectedBrandUrl: string | null
  currentJobId: string | null
  currentJob: AdGenJob | null
  compAnalysisJobId: string | null
  artifacts: OnboardingArtifacts
  currentSlideIndex: number
  error: string | null
}

interface OnboardingContextType {
  state: OnboardingState
  startOnboarding: (brandUrl: string) => Promise<void>
  completeOnboarding: () => Promise<void>
  setSlideIndex: (index: number) => void
  nextSlide: () => void
  prevSlide: () => void
  retry: () => void
  isLoading: boolean
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

const INITIAL_STATE: OnboardingState = {
  status: 'loading',
  selectedBrandUrl: null,
  currentJobId: null,
  currentJob: null,
  compAnalysisJobId: null,
  artifacts: {
    brand: null,
    metaIdentity: null,
    adsCollection: null,
    patterns: null,
    strategy: null,
    blueprint: null,
    clipBank: null,
    winner: null,
    competitorAnalysis: null,
    productBible: null,
  },
  currentSlideIndex: 0,
  error: null,
}

const TOTAL_SLIDES = 10

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [state, setState] = useState<OnboardingState>(INITIAL_STATE)
  const [pollInterval, setPollInterval] = useState<ReturnType<typeof setInterval> | null>(null)
  const hasInitialized = useRef(false)

  // Load artifacts from completed job (defined first so it can be used by other callbacks)
  const loadArtifacts = useCallback(async (job: AdGenJob, compJobId?: string | null) => {
    console.log('[Onboarding] Loading artifacts for job:', job.job_id)

    const artifacts: OnboardingArtifacts = {
      brand: null,
      metaIdentity: null,
      adsCollection: null,
      patterns: null,
      strategy: null,
      blueprint: null,
      clipBank: null,
      winner: job.winner || null,
      competitorAnalysis: null,
      productBible: null,
    }

    // Load all artifacts in parallel for speed
    const [brandResult, metaResult, adsResult, patternsResult, strategyResult, blueprintResult, clipBankResult, productBibleResult] = await Promise.allSettled([
      adGeneratorService.getStageDetail(job.job_id, 'brand_discovery'),
      adGeneratorService.getStageDetail(job.job_id, 'meta_identity_resolution'),
      adGeneratorService.getStageDetail(job.job_id, 'brand_ad_collection'),
      adGeneratorService.getStageDetail(job.job_id, 'competitor_intelligence'),
      adGeneratorService.getStageDetail(job.job_id, 'strategy_selection'),
      adGeneratorService.getStageDetail(job.job_id, 'creative_blueprint'),
      adGeneratorService.getStageDetail(job.job_id, 'clip_bank_creation'),
      adGeneratorService.getStageDetail(job.job_id, 'product_bible'),
    ])

    if (brandResult.status === 'fulfilled') {
      console.log('[Onboarding] Brand artifact:', brandResult.value.artifact)
      artifacts.brand = brandResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load brand artifact:', brandResult.reason)
    }

    if (metaResult.status === 'fulfilled') {
      console.log('[Onboarding] Meta identity artifact:', metaResult.value.artifact)
      artifacts.metaIdentity = metaResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load meta identity artifact:', metaResult.reason)
    }

    if (adsResult.status === 'fulfilled') {
      console.log('[Onboarding] Ads collection artifact:', adsResult.value.artifact)
      artifacts.adsCollection = adsResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load ads collection artifact:', adsResult.reason)
    }

    if (patternsResult.status === 'fulfilled') {
      console.log('[Onboarding] Patterns artifact:', patternsResult.value.artifact)
      artifacts.patterns = patternsResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load patterns artifact:', patternsResult.reason)
    }

    if (strategyResult.status === 'fulfilled') {
      console.log('[Onboarding] Strategy artifact:', strategyResult.value.artifact)
      artifacts.strategy = strategyResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load strategy artifact:', strategyResult.reason)
    }

    if (blueprintResult.status === 'fulfilled') {
      console.log('[Onboarding] Blueprint artifact:', blueprintResult.value.artifact)
      artifacts.blueprint = blueprintResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load blueprint artifact:', blueprintResult.reason)
    }

    if (clipBankResult.status === 'fulfilled') {
      console.log('[Onboarding] Clip bank artifact:', clipBankResult.value.artifact)
      artifacts.clipBank = clipBankResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load clip bank artifact:', clipBankResult.reason)
    }

    if (productBibleResult.status === 'fulfilled') {
      console.log('[Onboarding] Product bible artifact:', productBibleResult.value.artifact)
      artifacts.productBible = productBibleResult.value.artifact || null
    } else {
      console.warn('[Onboarding] Failed to load product bible artifact:', productBibleResult.reason)
    }

    // Load competitor analysis - wait for completion
    const brandUrl = job.url || (artifacts.brand as { url?: string })?.url
    if (brandUrl) {
      try {
        const competitorResult = await loadCompetitorAnalysisWithPolling(brandUrl, compJobId)
        if (competitorResult) {
          console.log('[Onboarding] Competitor analysis loaded:', competitorResult)
          artifacts.competitorAnalysis = competitorResult
        }
      } catch (error) {
        console.warn('[Onboarding] Failed to load competitor analysis:', error)
      }
    }

    console.log('[Onboarding] Final artifacts:', artifacts)
    setState(prev => ({ ...prev, artifacts }))
  }, [])

  // Helper: Load existing competitor analysis (jobs are created in startOnboarding)
  const loadCompetitorAnalysisWithPolling = async (brandUrl: string, existingJobId?: string | null) => {
    // If we have a job ID from startOnboarding, poll it directly
    if (existingJobId) {
      console.log('[Onboarding] Polling existing competitor analysis job:', existingJobId)
      return await pollCompetitorJob(existingJobId)
    }

    // Check for existing completed or running job (don't create new ones here)
    try {
      const jobsResponse = await competitorAnalysisService.listJobs()

      // First check for completed job
      const completedJob = jobsResponse.jobs.find(
        j => j.status === 'completed' && j.brand_url === brandUrl && j.result
      )
      if (completedJob?.result) {
        console.log('[Onboarding] Found completed competitor analysis:', completedJob.job_id)
        return completedJob.result
      }

      // Check for running job
      const runningJob = jobsResponse.jobs.find(
        j => (j.status === 'running' || j.status === 'pending') && j.brand_url === brandUrl
      )
      if (runningJob) {
        console.log('[Onboarding] Found running competitor analysis, waiting:', runningJob.job_id)
        return await pollCompetitorJob(runningJob.job_id)
      }

      console.log('[Onboarding] No competitor analysis found for brand')
      return null
    } catch (err) {
      console.warn('[Onboarding] Failed to check existing competitor jobs:', err)
      return null
    }
  }

  // Poll competitor job until completion
  const pollCompetitorJob = async (jobId: string, maxAttempts = 60) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const job = await competitorAnalysisService.getJob(jobId)
        if (job.status === 'completed' && job.result) {
          console.log('[Onboarding] Competitor analysis completed:', jobId)
          return job.result
        }
        if (job.status === 'failed' || job.status === 'cancelled') {
          console.warn('[Onboarding] Competitor analysis failed:', job.status)
          return null
        }
        // Wait 3 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 3000))
      } catch (err) {
        console.error('[Onboarding] Error polling competitor analysis:', err)
        return null
      }
    }
    console.warn('[Onboarding] Competitor analysis timed out')
    return null
  }

  // Track stale polls — if progress doesn't change after N polls, treat as stale
  const stalePollCount = useRef(0)
  const lastSeenProgress = useRef(-1)
  const STALE_POLL_THRESHOLD = 10 // ~30s at 3s intervals

  // Poll job status
  const pollJobStatus = useCallback(async (jobId: string) => {
    let skipPoll = false
    setState(prev => {
      if (prev.status === 'presenting') {
        skipPoll = true
      }
      return prev
    })
    if (skipPoll) {
      if (pollInterval) {
        clearInterval(pollInterval)
        setPollInterval(null)
      }
      return
    }

    try {
      const job = await adGeneratorService.getJob(jobId)

      console.log(`[Onboarding] Poll #${stalePollCount.current + 1}: job=${jobId.slice(0,8)} status=${job.status} progress=${job.progress_pct}% stage=${job.current_stage || 'none'}`)

      setState(prev => {
        if (prev.status === 'presenting') return prev
        return { ...prev, currentJob: job }
      })

      // Stale job detection: if progress hasn't changed, count up
      if (job.status === 'running' || job.status === 'pending') {
        if (job.progress_pct === lastSeenProgress.current) {
          stalePollCount.current++
        } else {
          stalePollCount.current = 0
          lastSeenProgress.current = job.progress_pct
        }
        if (stalePollCount.current >= STALE_POLL_THRESHOLD) {
          console.error(`[Onboarding] Job STALE — stuck at ${job.progress_pct}% (stage: ${job.current_stage}) for ${STALE_POLL_THRESHOLD * 3}s`)
          if (pollInterval) {
            clearInterval(pollInterval)
            setPollInterval(null)
          }
          setState(prev => ({
            ...prev,
            error: 'The pipeline appears to be stuck. Please try again.',
          }))
          stalePollCount.current = 0
          return
        }
      }

      if (job.status === 'completed') {
        // Stop polling immediately
        if (pollInterval) {
          clearInterval(pollInterval)
          setPollInterval(null)
        }
        // Get the competitor analysis job ID from state
        let compJobId: string | null = null
        setState(prev => {
          compJobId = prev.compAnalysisJobId
          return prev
        })
        // Load artifacts and transition to presentation
        await loadArtifacts(job, compJobId)
        setState(prev => ({
          ...prev,
          status: 'presenting',
          // Only set to 0 if not already presenting
          currentSlideIndex: prev.status === 'presenting' ? prev.currentSlideIndex : 0,
        }))
      } else if (job.status === 'failed' || job.status === 'cancelled') {
        // Stop polling on failure
        if (pollInterval) {
          clearInterval(pollInterval)
          setPollInterval(null)
        }
        setState(prev => ({
          ...prev,
          error: job.error_message || 'Job failed. Please try again.',
        }))
      }
    } catch (error) {
      console.error('[Onboarding] Poll error:', error)
    }
  }, [pollInterval, loadArtifacts])

  // Check onboarding status from API
  const checkOnboardingStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setState(prev => ({ ...prev, status: 'loading' }))
      return
    }

    // Skip if already in presenting mode - don't make API calls that could reset state
    // Using a state check here to avoid stale closure issues
    let skipCheck = false
    setState(prev => {
      if (prev.status === 'presenting') {
        skipCheck = true
      }
      return prev
    })
    if (skipCheck) {
      console.log('[Onboarding] Skipping status check - already presenting')
      return
    }

    try {
      const response = await apiClient.get('/marketing/onboarding/status')
      const data = response.data

      if (data.status === 'completed') {
        setState(prev => {
          // Don't change if already presenting
          if (prev.status === 'presenting') return prev
          return { ...prev, status: 'completed' }
        })
      } else if (data.status === 'in_progress' && data.job_id) {
        // Resume in-progress job
        setState(prev => ({
          ...prev,
          status: prev.status === 'presenting' ? 'presenting' : 'pipeline_running',
          currentJobId: data.job_id,
        }))
        // Fetch the job to resume
        const job = await adGeneratorService.getJob(data.job_id)
        setState(prev => ({ ...prev, currentJob: job }))

        if (job.status === 'completed') {
          // Job is done, load artifacts and go to presentation
          let compJobId: string | null = null
          setState(prev => {
            compJobId = prev.compAnalysisJobId
            return prev
          })
          await loadArtifacts(job, compJobId)
          setState(prev => ({
            ...prev,
            status: 'presenting',
            currentSlideIndex: prev.status === 'presenting' ? prev.currentSlideIndex : 0,
          }))
        } else if (job.status !== 'failed' && job.status !== 'cancelled') {
          // Job is still running — start polling (this is the resume-on-refresh case)
          const interval = setInterval(() => {
            pollJobStatus(data.job_id)
          }, 3000)
          setPollInterval(interval)
        }
      } else {
        // Not started
        setState(prev => {
          // Don't change if already presenting
          if (prev.status === 'presenting') return prev
          return { ...prev, status: 'brand_select' }
        })
      }
    } catch (error) {
      // If endpoint doesn't exist yet, default to brand_select for new users
      console.warn('[Onboarding] Status check failed, defaulting to brand_select:', error)
      setState(prev => {
        // Don't change if already presenting
        if (prev.status === 'presenting') return prev
        return { ...prev, status: 'brand_select' }
      })
    }
  }, [isAuthenticated, loadArtifacts])

  // Start onboarding with a brand URL
  const startOnboarding = useCallback(async (brandUrl: string) => {
    setState(prev => ({
      ...prev,
      selectedBrandUrl: brandUrl,
      error: null,
    }))

    // Start competitor analysis job in parallel (fire and forget)
    competitorAnalysisService.createJob({ brand_url: brandUrl })
      .then(compJob => {
        console.log('[Onboarding] Started competitor analysis job:', compJob.job_id)
        setState(prev => ({ ...prev, compAnalysisJobId: compJob.job_id }))
      })
      .catch(err => {
        console.warn('[Onboarding] Failed to start competitor analysis (non-blocking):', err)
      })

    try {
      // Notify backend that onboarding has started
      const startResponse = await apiClient.post('/marketing/onboarding/start', { brand_url: brandUrl })
      const jobData = startResponse.data.job || startResponse.data

      setState(prev => ({
        ...prev,
        status: 'pipeline_running',
        currentJobId: jobData.job_id,
        currentJob: jobData,
      }))

      // Start polling
      const interval = setInterval(() => {
        pollJobStatus(jobData.job_id)
      }, 3000)
      setPollInterval(interval)
    } catch (error) {
      // Fallback: create job directly if onboarding endpoint not available
      try {
        const job = await adGeneratorService.createJob({ url: brandUrl })
        setState(prev => ({
          ...prev,
          status: 'pipeline_running',
          currentJobId: job.job_id,
          currentJob: job,
        }))

        const interval = setInterval(() => {
          pollJobStatus(job.job_id)
        }, 3000)
        setPollInterval(interval)
      } catch (fallbackError) {
        console.error('[Onboarding] Failed to start:', fallbackError)
        setState(prev => ({
          ...prev,
          error: fallbackError instanceof Error ? fallbackError.message : 'Failed to start generation',
        }))
      }
    }
  }, [pollJobStatus])

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    try {
      await apiClient.post('/marketing/onboarding/complete', {
        job_id: state.currentJobId,
      })
    } catch {
      // Ignore errors - backend endpoint may not exist yet
      console.warn('[Onboarding] Complete endpoint not available')
    }

    setState(prev => ({ ...prev, status: 'completed' }))
    // Navigate to marketing home
    navigate(ROUTES.MARKETING.HOME, { replace: true })
  }, [state.currentJobId, navigate])

  // Slide navigation
  const setSlideIndex = useCallback((index: number) => {
    if (index >= 0 && index < TOTAL_SLIDES) {
      setState(prev => ({ ...prev, currentSlideIndex: index }))
    }
  }, [])

  const nextSlide = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentSlideIndex: Math.min(prev.currentSlideIndex + 1, TOTAL_SLIDES - 1),
    }))
  }, [])

  const prevSlide = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentSlideIndex: Math.max(prev.currentSlideIndex - 1, 0),
    }))
  }, [])

  // Retry after failure — reset backend onboarding status and go back to brand select
  const retry = useCallback(async () => {
    console.log('[Onboarding] Retry triggered — resetting onboarding status')
    // Stop any active polling
    if (pollInterval) {
      clearInterval(pollInterval)
      setPollInterval(null)
    }
    stalePollCount.current = 0
    lastSeenProgress.current = -1

    try {
      await apiClient.post('/marketing/onboarding/reset')
      console.log('[Onboarding] Backend onboarding status reset')
    } catch (err) {
      console.warn('[Onboarding] Reset endpoint failed (non-blocking):', err)
    }

    setState(prev => ({
      ...prev,
      status: 'brand_select',
      currentJob: null,
      currentJobId: null,
      compAnalysisJobId: null,
      error: null,
    }))
  }, [pollInterval])

  // Check status on mount (only once)
  useEffect(() => {
    if (!authLoading && isAuthenticated && !hasInitialized.current) {
      hasInitialized.current = true
      checkOnboardingStatus()
    }
  }, [authLoading, isAuthenticated, checkOnboardingStatus])

  // Stop polling when entering presenting mode
  useEffect(() => {
    if (state.status === 'presenting' && pollInterval) {
      clearInterval(pollInterval)
      setPollInterval(null)
    }
  }, [state.status, pollInterval])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [pollInterval])

  return (
    <OnboardingContext.Provider
      value={{
        state,
        startOnboarding,
        completeOnboarding,
        setSlideIndex,
        nextSlide,
        prevSlide,
        retry,
        isLoading: state.status === 'loading',
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider')
  }
  return context
}
